/**
 * Mock content for the product-grounded concepts.
 *
 * Every value here was read off the authenticated mlx-demo application on
 * 2026-08-23 and then retyped as a fixture, so the concepts render real
 * product shapes without reaching for a live tenant. Two rules:
 *
 *  - The customer names are Xero's own demo organisation (Ridgeway University,
 *    Boom FM, City Limousines, Basket Shop, Marine Systems …). They are sample
 *    data shipped by Xero, not an MLX customer. Never substitute a real one.
 *  - The figures are a real run against that sample data. They are still
 *    labelled "Illustrative specimen" wherever they appear, because the point
 *    of the exhibit is the shape of a governed answer, not the number.
 */

import type { ChannelDataFreshness } from "@mlx/contracts";
import type { PluginIconPlugin } from "@product/components/content/plugin-icon";
import type { RunArtifact } from "@product/lib/run-artifacts";
import type { RunSourceUsage } from "@product/lib/run-source-usage";


/** The response body, as the product's markdown renderer receives it. */
export const RECEIVABLES_ANSWER_MARKDOWN = `### Aged receivables by customer

**As at 11 August 2026, GBP, ranked by total outstanding**

| Rank | Customer | Invoices | Total AR | Current | 1–30 days | 31–60 days | 61–90 days |
| ---: | --- | ---: | ---: | ---: | ---: | ---: | ---: |
| 1 | Ridgeway University | 1 | £6,187.50 | £0.00 | £6,187.50 | £0.00 | £0.00 |
| 2 | DIISR – Small Business Services | 2 | £4,470.63 | £4,200.00 | £0.00 | £270.63 | £0.00 |
| 3 | Bayside Club | 2 | £3,434.00 | £3,434.00 | £0.00 | £0.00 | £0.00 |
| 4 | Port & Philip Freight | 2 | £2,245.00 | £0.00 | £2,245.00 | £0.00 | £0.00 |
| 5 | Boom FM | 1 | £1,623.75 | £1,623.75 | £0.00 | £0.00 | £0.00 |
| 6 | City Limousines | 4 | £1,169.95 | £650.00 | £0.00 | £269.95 | £250.00 |
| 7 | Basket Shop | 1 | £914.55 | £914.55 | £0.00 | £0.00 | £0.00 |
| 8 | Hamilton Smith Ltd | 1 | £550.00 | £550.00 | £0.00 | £0.00 | £0.00 |
| 9 | Rex Media Group | 1 | £550.00 | £550.00 | £0.00 | £0.00 | £0.00 |
| 10 | Marine Systems | 1 | £396.00 | £396.00 | £0.00 | £0.00 | £0.00 |
| | **Total** | **16** | **£21,541.38** | **£12,318.30** | **£8,432.50** | **£540.58** | **£250.00** |

- **Overdue:** £9,223.08, or **42.8%** of total receivables.
- **Collection priority:** Ridgeway University and Port & Philip Freight account for **£8,432.50** of the overdue balance.
- No 91+ day balances or missing due dates were found.

Basis: Xero sales invoices with a positive live \`AmountDue\`; voided and deleted
invoices excluded. Aging uses invoice due dates. The synced invoice snapshot was
last updated **11 August 2026 at approximately 06:53 UTC**.
`;

export const RECEIVABLES_QUESTION =
	"show aged receivables, ranked by value per customer";

/** The receipt row beneath a governed answer: label, then the sources used. */
export const RECEIVABLES_RECEIPT = {
	label: "Retrieved with",
	sources: [
		{ kind: "warehouse", name: "MLX warehouse" },
		{ kind: "plugin", name: "Xero" },
	],
	actions: ["Evaluate with Twin", "Trace", "Branch"],
	runLabel: "Run trace · 44 updates · 128219ms",
} as const;

/** The governed non-answerable state, using the product's own strings. */
export const REFUSAL_FIXTURE = {
	question:
		"Which suppliers will we be most exposed to next quarter, and by how much?",
	headline: "I couldn't answer that from the available business data",
	body: "The question asks for a forward projection. No published business context supports one, so no number is offered.",
	suggestedFollowUpLabel: "You could try:",
	suggestedFollowUp:
		"Which supplier balances are overdue by more than 60 days?",
	exploreLabel: "Let the agent explore",
	hardStop:
		"MLX stopped instead of returning an unsupported result.",
} as const;

/* ------------------------------------------------------------------ */
/*  Channel, Data Set and views                                        */
/* ------------------------------------------------------------------ */

export const CHANNEL_FIXTURE = {
	name: "Renewal Operations Demo",
	sections: [
		"Data sets",
		"Feed",
		"Sources",
		"Memory",
		"Files",
		"Artifacts",
		"About this channel",
	],
	freshness: {
		state: "Data is fresh",
		rows: [
			["Data as of", "11 August 2026, 06:53"],
			["Last ingestion", "2 hours ago"],
			["Projection refresh", "1 hour ago"],
			["Next scheduled", "in 58 minutes"],
			["Why", "Within the freshness target"],
		],
	},
	views: [
		{ name: "Renewal command", kind: "Table" },
		{ name: "Renewal stages", kind: "Board" },
		{ name: "Renewal overview", kind: "Dashboard" },
	],
} as const;

export type RenewalRecord = {
	title: string;
	health: "On track" | "At risk" | "Watch";
	annualValue: string;
	renewalDate: string;
	probability: string;
	accountLead: string;
	nextStep: string;
	stage: "Discovery" | "Validation" | "Commercial" | "Contracting" | "Renewed";
};

/** The Data Set behind every view. Fictional accounts created for review. */
export const RENEWAL_RECORDS: RenewalRecord[] = [
	{ title: "Orbit Education", health: "On track", annualValue: "£31,000", renewalDate: "Aug 27, 2026", probability: "100%", accountLead: "Maya Chen", nextStep: "Schedule adoption review", stage: "Renewed" },
	{ title: "Alder & Co Logistics", health: "On track", annualValue: "£48,000", renewalDate: "Aug 31, 2026", probability: "100%", accountLead: "Sam Okafor", nextStep: "Launch success plan", stage: "Renewed" },
	{ title: "Solway Gridworks", health: "At risk", annualValue: "£84,000", renewalDate: "Sep 14, 2026", probability: "72%", accountLead: "Maya Chen", nextStep: "Resolve DPA redlines", stage: "Contracting" },
	{ title: "Brightwater Energy", health: "On track", annualValue: "£96,000", renewalDate: "Sep 22, 2026", probability: "86%", accountLead: "Elena Rossi", nextStep: "Finalise deployment region", stage: "Contracting" },
	{ title: "Halcyon Fleet", health: "Watch", annualValue: "£62,000", renewalDate: "Sep 28, 2026", probability: "64%", accountLead: "Noah Patel", nextStep: "Confirm expansion seats", stage: "Commercial" },
	{ title: "Lumen Mobility", health: "At risk", annualValue: "£125,000", renewalDate: "Sep 30, 2026", probability: "49%", accountLead: "Noah Patel", nextStep: "Confirm security sponsor", stage: "Commercial" },
	{ title: "Arcstone Legal", health: "On track", annualValue: "£69,000", renewalDate: "Oct 1, 2026", probability: "82%", accountLead: "Sam Okafor", nextStep: "Issue final order form", stage: "Commercial" },
	{ title: "Juniper Health Systems", health: "At risk", annualValue: "£118,000", renewalDate: "Oct 5, 2026", probability: "55%", accountLead: "Elena Rossi", nextStep: "Run evidence workshop", stage: "Validation" },
	{ title: "Copperline Retail", health: "Watch", annualValue: "£54,000", renewalDate: "Oct 12, 2026", probability: "58%", accountLead: "Sam Okafor", nextStep: "Validate returns metric", stage: "Validation" },
	{ title: "Meridian Foods", health: "On track", annualValue: "£73,000", renewalDate: "Oct 18, 2026", probability: "78%", accountLead: "Maya Chen", nextStep: "Share ROI model", stage: "Validation" },
	{ title: "Kestrel Manufacturing", health: "Watch", annualValue: "£39,000", renewalDate: "Nov 3, 2026", probability: "42%", accountLead: "Noah Patel", nextStep: "Map ERP sources", stage: "Discovery" },
	{ title: "Fernway Property", health: "Watch", annualValue: "£44,000", renewalDate: "Nov 16, 2026", probability: "38%", accountLead: "Elena Rossi", nextStep: "Agree pilot scope", stage: "Discovery" },
];

/** The dashboard widgets over the same Data Set. */
export const RENEWAL_DASHBOARD = {
	tiles: [
		{ label: "Portfolio value", value: "GBP 843.0k", detail: "Annual value · Sum" },
		{ label: "Renewal accounts", value: "12", detail: "Records · Count" },
		{ label: "Average probability", value: "68.7%", detail: "Renewal probability · Average" },
	],
	valueByStage: [
		{ stage: "Commercial", value: 256000 },
		{ stage: "Contracting", value: 180000 },
		{ stage: "Validation", value: 245000 },
		{ stage: "Discovery", value: 83000 },
		{ stage: "Renewed", value: 79000 },
	],
	health: [
		{ label: "On track", count: 5 },
		{ label: "Watch", count: 4 },
		{ label: "At risk", count: 3 },
	],
} as const;

/* ------------------------------------------------------------------ */
/*  The run, one level down: invoices, receipt, artifacts, freshness   */
/* ------------------------------------------------------------------ */

export type InvoiceRow = {
	number: string;
	customer: string;
	dueDate: string;
	status: "Awaiting payment" | "Overdue";
	amountDue: string;
};

/**
 * The source ledger SES-004 shows above the governed answer. Per-customer
 * invoice counts, totals and aging buckets reconcile exactly with the
 * recorded run's aging table — including the £250.00 61–90-day City
 * Limousines invoice that the table's printed columns stop short of but its
 * Total AR includes. How a customer's bucket splits across individual
 * invoices is illustrative; every other figure is the run's. Aging is as at
 * 11 August 2026, by due date; rows are due-date ascending.
 */
export const INVOICE_ROWS: InvoiceRow[] = [
	{ number: "INV-0018", customer: "City Limousines", dueDate: "May 30, 2026", status: "Overdue", amountDue: "£250.00" },
	{ number: "INV-0021", customer: "City Limousines", dueDate: "Jun 26, 2026", status: "Overdue", amountDue: "£150.00" },
	{ number: "INV-0022", customer: "DIISR – Small Business Services", dueDate: "Jun 26, 2026", status: "Overdue", amountDue: "£270.63" },
	{ number: "INV-0024", customer: "City Limousines", dueDate: "Jul 8, 2026", status: "Overdue", amountDue: "£119.95" },
	{ number: "INV-0026", customer: "Port & Philip Freight", dueDate: "Jul 22, 2026", status: "Overdue", amountDue: "£1,250.00" },
	{ number: "INV-0027", customer: "Ridgeway University", dueDate: "Jul 28, 2026", status: "Overdue", amountDue: "£6,187.50" },
	{ number: "INV-0029", customer: "Port & Philip Freight", dueDate: "Aug 3, 2026", status: "Overdue", amountDue: "£995.00" },
	{ number: "INV-0030", customer: "Marine Systems", dueDate: "Aug 15, 2026", status: "Awaiting payment", amountDue: "£396.00" },
	{ number: "INV-0031", customer: "Hamilton Smith Ltd", dueDate: "Aug 19, 2026", status: "Awaiting payment", amountDue: "£550.00" },
	{ number: "INV-0032", customer: "Bayside Club", dueDate: "Aug 20, 2026", status: "Awaiting payment", amountDue: "£2,100.00" },
	{ number: "INV-0033", customer: "City Limousines", dueDate: "Aug 24, 2026", status: "Awaiting payment", amountDue: "£650.00" },
	{ number: "INV-0034", customer: "Boom FM", dueDate: "Aug 25, 2026", status: "Awaiting payment", amountDue: "£1,623.75" },
	{ number: "INV-0035", customer: "Rex Media Group", dueDate: "Aug 27, 2026", status: "Awaiting payment", amountDue: "£550.00" },
	{ number: "INV-0036", customer: "DIISR – Small Business Services", dueDate: "Aug 30, 2026", status: "Awaiting payment", amountDue: "£4,200.00" },
	{ number: "INV-0037", customer: "Basket Shop", dueDate: "Sep 2, 2026", status: "Awaiting payment", amountDue: "£914.55" },
	{ number: "INV-0038", customer: "Bayside Club", dueDate: "Sep 5, 2026", status: "Awaiting payment", amountDue: "£1,334.00" },
];

/**
 * The receipt beneath the governed answer, in the shape the real
 * RunSourcesUsedView takes. The run retrieved through the MLX warehouse and
 * the Xero plugin (as RECEIVABLES_RECEIPT records); the per-source call
 * counts are illustrative — the run ledger records "44 updates" without a
 * per-source split.
 */
export const RECEIPT_SOURCES: RunSourceUsage[] = [
	{ key: "warehouse", kind: "warehouse", label: "MLX warehouse", callCount: 3, documentCount: 0, searchCount: 0, fetchCount: 0 },
	{ key: "plugin:xero", kind: "plugin", pluginSlug: "xero", label: "Xero", callCount: 2, documentCount: 0, searchCount: 0, fetchCount: 0 },
];

/** Static stand-in for the runtime plugin catalogue: Xero's branding only. */
export const RECEIPT_PLUGINS: PluginIconPlugin[] = [
	{
		slug: "xero",
		label: "Xero",
		branding: { iconUrl: "/concepts/source-icons/xero.svg" },
	},
];

/**
 * The two artifacts the run wrote, in the shape the real ArtifactCard takes.
 * Titles, kinds and the v1 are the run's; the accent gradient is the
 * product's own for MLX-written documents and spreadsheets
 * (apps/app/src/lib/run-artifacts.ts).
 */
export const RECEIVABLES_ARTIFACTS: RunArtifact[] = [
	{
		id: "concept-receivables-document",
		title: "Aged receivables by customer",
		kindLabel: "Document",
		icon: "file-text",
		accentClassName: "bg-gradient-to-br from-[#EA7C34] to-[#C2590A]",
		contentKind: "markdown",
		version: 1,
		operation: "created",
	},
	{
		id: "concept-receivables-spreadsheet",
		title: "Aged receivables, 11 August 2026",
		kindLabel: "Spreadsheet",
		icon: "file-spreadsheet",
		accentClassName: "bg-gradient-to-br from-[#EA7C34] to-[#C2590A]",
		contentKind: "csv",
		version: 1,
		operation: "created",
	},
];

/* ------------------------------------------------------------------ */
/*  Channel freshness, in the contract shape                           */
/* ------------------------------------------------------------------ */

const FIXTURE_NOW = Date.now();
const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;

/**
 * Two freshness states for the real ChannelDataFreshnessIndicator, echoing
 * the reconstruction's rows ("Last ingestion 2 hours ago", "Projection
 * refresh 1 hour ago", "Next scheduled in 58 minutes"). Timestamps are
 * offsets from module-load time so the component's relative rendering reads
 * the same on every visit; offsets sit mid-bucket so the printed strings are
 * stable across server render, hydration and snapshot runs.
 */
export const FRESHNESS_FRESH: ChannelDataFreshness = {
	kind: "exact_connector",
	status: "fresh",
	generatedAt: FIXTURE_NOW - 40_000,
	dataAsOf: FIXTURE_NOW - (2 * HOUR_MS + 10 * MINUTE_MS),
	ageMs: 2 * HOUR_MS + 10 * MINUTE_MS,
	maxStalenessMs: 6 * HOUR_MS,
	lastIngestionAt: FIXTURE_NOW - (2 * HOUR_MS + 5 * MINUTE_MS),
	lastProjectionRefreshAt: FIXTURE_NOW - (HOUR_MS + 2 * MINUTE_MS),
	nextScheduledAt: FIXTURE_NOW + 58 * MINUTE_MS,
	nextRetryAt: null,
	snapshotCapturedAt: null,
	snapshotExpiresAt: null,
	desiredProjectionWatermark: null,
	servedProjectionWatermark: null,
	sourceSystems: ["xero"],
	sourceWatermarks: [
		{
			sourceSystem: "xero",
			sourceWatermark: FIXTURE_NOW - (2 * HOUR_MS + 10 * MINUTE_MS),
			observedThrough: FIXTURE_NOW - (2 * HOUR_MS + 10 * MINUTE_MS),
			lastIngestionAt: FIXTURE_NOW - (2 * HOUR_MS + 5 * MINUTE_MS),
			nextScheduledAt: FIXTURE_NOW + 58 * MINUTE_MS,
			nextRetryAt: null,
			status: "fresh",
		},
	],
	reasonCodes: ["within_target"],
	materialized: true,
};

export const FRESHNESS_STALE: ChannelDataFreshness = {
	kind: "exact_connector",
	status: "stale",
	generatedAt: FIXTURE_NOW - 40_000,
	dataAsOf: FIXTURE_NOW - (9 * HOUR_MS + 10 * MINUTE_MS),
	ageMs: 9 * HOUR_MS + 10 * MINUTE_MS,
	maxStalenessMs: 6 * HOUR_MS,
	lastIngestionAt: FIXTURE_NOW - (9 * HOUR_MS + 5 * MINUTE_MS),
	lastProjectionRefreshAt: FIXTURE_NOW - (8 * HOUR_MS + 30 * MINUTE_MS),
	nextScheduledAt: null,
	nextRetryAt: FIXTURE_NOW + 12 * MINUTE_MS,
	snapshotCapturedAt: null,
	snapshotExpiresAt: null,
	desiredProjectionWatermark: null,
	servedProjectionWatermark: null,
	sourceSystems: ["xero"],
	sourceWatermarks: [
		{
			sourceSystem: "xero",
			sourceWatermark: FIXTURE_NOW - (9 * HOUR_MS + 10 * MINUTE_MS),
			observedThrough: FIXTURE_NOW - (9 * HOUR_MS + 10 * MINUTE_MS),
			lastIngestionAt: FIXTURE_NOW - (9 * HOUR_MS + 5 * MINUTE_MS),
			nextScheduledAt: null,
			nextRetryAt: FIXTURE_NOW + 12 * MINUTE_MS,
			status: "stale",
		},
	],
	reasonCodes: ["source_stale"],
	materialized: true,
};
